import { useState, useEffect, useMemo } from "react";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  doc,
  deleteDoc,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useHouseholdContext } from "@/contexts/HouseholdContext";
import { useI18n } from "@/contexts/I18nContext";
import { fromTimestamp } from "@/lib/firestore";
import { Card } from "@/components/ui/Card";
import { EmptyState } from "@/components/ui/EmptyState";
import { CreateEventModal } from "@/components/features/CreateEventModal";
import { CalendarSyncSettingsModal } from "@/components/features/CalendarSyncSettingsModal";
import type { CalendarEvents } from "@/lib/models";
import {
  dayKey,
  dayKeysForEvent,
  eventOverlapsDay,
  isAllDayEvent,
  sameDay,
} from "@/lib/calendar";

function getWeekdayLabels(locale: string): string[] {
  const fmt = new Intl.DateTimeFormat(locale, { weekday: "short" });
  // 2024-01-07 was a Sunday — anchor a known week to derive locale labels.
  const sunday = new Date(2024, 0, 7);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(sunday);
    d.setDate(sunday.getDate() + i);
    return fmt.format(d);
  });
}

function formatTime(date: Date, locale: string): string {
  return date.toLocaleTimeString(locale, {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function isReadOnlySource(source?: string | null): boolean {
  return source === "ical" || source === "eventkit";
}

function getDaysInMonth(year: number, month: number): Date[] {
  const days: Date[] = [];
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);

  // Pad start of month with previous month's days
  for (let i = 0; i < first.getDay(); i++) {
    const d = new Date(year, month, -first.getDay() + i + 1);
    days.push(d);
  }

  for (let d = 1; d <= last.getDate(); d++) {
    days.push(new Date(year, month, d));
  }

  // Pad end to fill 6 rows
  const remaining = 42 - days.length;
  for (let i = 1; i <= remaining; i++) {
    days.push(new Date(year, month + 1, i));
  }

  return days;
}

export function EventsPage() {
  const { householdId, loading: contextLoading } = useHouseholdContext();
  const { t, locale } = useI18n();
  const weekdayLabels = useMemo(() => getWeekdayLabels(locale), [locale]);
  const [events, setEvents] = useState<CalendarEvents[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [currentMonth, setCurrentMonth] = useState(() => new Date());
  const [showModal, setShowModal] = useState(false);
  const [editEvent, setEditEvent] = useState<CalendarEvents | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [view, setView] = useState<"calendar" | "list">("calendar");
  const [searchText, setSearchText] = useState("");
  const [sourceFilter, setSourceFilter] = useState<string>("all");
  const [showSyncSettings, setShowSyncSettings] = useState(false);

  useEffect(() => {
    if (!householdId) {
      setLoading(false);
      return;
    }

    const ref = collection(db, `households/${householdId}/events`);
    const q = query(ref, orderBy("start", "asc"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const items = snapshot.docs.map(
          (d) => ({ ...d.data(), uid: d.id }) as CalendarEvents
        );
        setEvents(items);
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching events:", error);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [householdId]);

  const days = useMemo(
    () =>
      getDaysInMonth(currentMonth.getFullYear(), currentMonth.getMonth()),
    [currentMonth]
  );

  // Stamp every day each event spans onto the calendar grid Set, not just
  // the start day. Fixes multi-day events showing dots only on day 1.
  // See [[tasks/zoey-59]].
  const eventDates = useMemo(() => {
    const set = new Set<string>();
    for (const ev of events) {
      const start = fromTimestamp(ev.start);
      const end = fromTimestamp(ev.end);
      for (const key of dayKeysForEvent(start, end)) {
        set.add(key);
      }
    }
    return set;
  }, [events]);

  // Range overlap (matches iOS / Android): events that span the selected day
  // appear in the list, even when their start was earlier or end is later.
  const selectedEvents = useMemo(() => {
    return events.filter((ev) =>
      eventOverlapsDay(
        fromTimestamp(ev.start),
        fromTimestamp(ev.end),
        selectedDate,
      ),
    );
  }, [events, selectedDate]);

  // Distinct event sources present in the household. Mirrors iOS
  // `EventsView.swift:33-40` `availableSources` computed property.
  const availableSources = useMemo(() => {
    const seen = new Set<string>();
    for (const ev of events) {
      const key = ev.sync_source ?? "manual";
      seen.add(key);
    }
    return Array.from(seen);
  }, [events]);

  // List-view filter pipeline: source filter → search.
  const filteredListEvents = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    return events.filter((ev) => {
      const source = ev.sync_source ?? "manual";
      if (sourceFilter !== "all" && source !== sourceFilter) return false;
      if (!q) return true;
      const haystack = [ev.text, ev.summary, ev.location, ev.description]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [events, searchText, sourceFilter]);

  // Group filtered list events by "Month YYYY" for the list view.
  // Mirrors iOS `EventsView.eventsByMonth` (`EventsView.swift:70-88`).
  const eventsByMonth = useMemo(() => {
    const groups: { key: string; label: string; events: CalendarEvents[] }[] = [];
    let currentKey = "";
    for (const ev of filteredListEvents) {
      const start = fromTimestamp(ev.start);
      if (!start) continue;
      const key = `${start.getFullYear()}-${start.getMonth()}`;
      if (key !== currentKey) {
        groups.push({
          key,
          label: start.toLocaleDateString(locale, {
            month: "long",
            year: "numeric",
          }),
          events: [],
        });
        currentKey = key;
      }
      groups[groups.length - 1].events.push(ev);
    }
    return groups;
  }, [filteredListEvents, locale]);

  const prevMonth = () => {
    setCurrentMonth(
      (m) => new Date(m.getFullYear(), m.getMonth() - 1, 1)
    );
  };

  const nextMonth = () => {
    setCurrentMonth(
      (m) => new Date(m.getFullYear(), m.getMonth() + 1, 1)
    );
  };

  const handleDelete = async (eventId: string) => {
    if (!householdId) return;
    // Source-aware guard — events synced from external calendars
    // (iCal, EventKit) are mirrored from a remote source and should
    // not be deletable from Zoey. Mirrors iOS
    // `EventsView.swift:240-243`. Defense in depth: the delete button
    // itself is hidden for these events; this is a backstop in case
    // it's reached via another path.
    const ev = events.find((e) => e.uid === eventId);
    if (ev && isReadOnlySource(ev.sync_source)) {
      setConfirmDeleteId(null);
      return;
    }
    try {
      await deleteDoc(doc(db, `households/${householdId}/events`, eventId));
      setConfirmDeleteId(null);
    } catch (error) {
      console.error("Error deleting event:", error);
    }
  };

  const handleEditEvent = (ev: CalendarEvents) => {
    setEditEvent(ev);
    setShowModal(true);
  };

  const handleCreateEvent = () => {
    setEditEvent(null);
    setShowModal(true);
  };

  if (loading || contextLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div
          className="w-8 h-8 border-3 border-t-transparent rounded-full animate-spin"
          style={{
            borderColor: "var(--accent-color, #00C7BE)",
            borderTopColor: "transparent",
          }}
        />
      </div>
    );
  }

  const today = new Date();
  const monthLabel = currentMonth.toLocaleString(locale, {
    month: "long",
    year: "numeric",
  });

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t("events.title")}</h1>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-gray-200 dark:border-white/10 overflow-hidden text-xs font-medium">
            <button
              type="button"
              onClick={() => setView("calendar")}
              className={`px-3 py-1.5 transition-colors ${
                view === "calendar"
                  ? "bg-[color:var(--accent-color)] text-white"
                  : "text-gray-500 hover:bg-gray-50 dark:hover:bg-white/5"
              }`}
            >
              {t("events.view.calendar")}
            </button>
            <button
              type="button"
              onClick={() => setView("list")}
              className={`px-3 py-1.5 transition-colors ${
                view === "list"
                  ? "bg-[color:var(--accent-color)] text-white"
                  : "text-gray-500 hover:bg-gray-50 dark:hover:bg-white/5"
              }`}
            >
              {t("events.view.list")}
            </button>
          </div>
          <button
            type="button"
            onClick={() => setShowSyncSettings(true)}
            aria-label={t("events.sync.title")}
            title={t("events.sync.title")}
            className="p-1.5 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 12a9 9 0 0 0-15-6.7L3 8" />
              <path d="M3 4v4h4" />
              <path d="M3 12a9 9 0 0 0 15 6.7L21 16" />
              <path d="M21 20v-4h-4" />
            </svg>
          </button>
          <button
            onClick={handleCreateEvent}
            className="px-3 py-1.5 rounded-lg text-white text-sm font-semibold"
            style={{ backgroundColor: "var(--accent-color, #00C7BE)" }}
          >
            {t("events.create.button")}
          </button>
        </div>
      </div>

      {/* Calendar Grid */}
      {view === "calendar" && (
      <Card>
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={prevMonth}
            aria-label={t("events.calendar.previousMonthAria")}
            className="p-1 text-gray-500 hover:text-gray-700"
          >
            &#8249;
          </button>
          <h2 className="font-semibold text-sm">{monthLabel}</h2>
          <button
            onClick={nextMonth}
            aria-label={t("events.calendar.nextMonthAria")}
            className="p-1 text-gray-500 hover:text-gray-700"
          >
            &#8250;
          </button>
        </div>

        <div className="grid grid-cols-7 gap-0">
          {weekdayLabels.map((d) => (
            <div
              key={d}
              className="text-center text-xs font-medium text-gray-400 py-1"
            >
              {d}
            </div>
          ))}
          {days.map((day, i) => {
            const isCurrentMonth =
              day.getMonth() === currentMonth.getMonth();
            const isToday = sameDay(day, today);
            const isSelected = sameDay(day, selectedDate);
            const hasEvents = eventDates.has(dayKey(day));

            return (
              <button
                key={i}
                onClick={() => setSelectedDate(new Date(day))}
                className={`relative flex flex-col items-center justify-center py-1.5 text-sm rounded-lg transition-colors ${
                  !isCurrentMonth ? "text-gray-300 dark:text-gray-600" : ""
                } ${isSelected ? "text-white font-bold" : ""} ${
                  isToday && !isSelected
                    ? "font-bold"
                    : ""
                }`}
                style={
                  isSelected
                    ? {
                        backgroundColor: "var(--accent-color, #00C7BE)",
                      }
                    : isToday
                    ? { color: "var(--accent-color, #00C7BE)" }
                    : undefined
                }
              >
                {day.getDate()}
                {hasEvents && (
                  <div
                    className="absolute bottom-0.5 w-1 h-1 rounded-full"
                    style={{
                      backgroundColor: isSelected
                        ? "white"
                        : "var(--accent-color, #00C7BE)",
                    }}
                  />
                )}
              </button>
            );
          })}
        </div>
      </Card>
      )}

      {/* List-view extras: search + source filter chips. Calendar
          view keeps the prior selected-day layout. */}
      {view === "list" && (
        <>
          <div className="relative">
            <input
              type="search"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder={t("events.search.placeholder")}
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm outline-none focus:ring-2 focus:ring-[color:var(--accent-color)]"
            />
            <svg
              className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="7" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </div>

          {availableSources.length > 1 && (
            <div className="flex gap-1.5 overflow-x-auto pb-1">
              <button
                type="button"
                onClick={() => setSourceFilter("all")}
                className={`shrink-0 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                  sourceFilter === "all"
                    ? "bg-[color:var(--accent-color)] text-white"
                    : "bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-300"
                }`}
              >
                {t("events.filter.all")}
              </button>
              {availableSources.map((src) => {
                const active = sourceFilter === src;
                const label =
                  src === "ical"
                    ? t("events.source.ical")
                    : src === "eventkit"
                    ? t("events.source.deviceCalendar")
                    : src === "manual"
                    ? t("events.source.manual")
                    : src;
                return (
                  <button
                    key={src}
                    type="button"
                    onClick={() => setSourceFilter(active ? "all" : src)}
                    className={`shrink-0 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                      active
                        ? "bg-[color:var(--accent-color)] text-white"
                        : "bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-300"
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Selected Day Events (calendar view) / Monthly-grouped list */}
      <div className="space-y-2">
        <p className="text-sm font-semibold text-gray-500 px-1">
          {view === "calendar"
            ? selectedDate.toLocaleDateString(locale, {
                weekday: "long",
                month: "long",
                day: "numeric",
              })
            : t("events.list.allUpcoming")}
        </p>

        {view === "calendar" ? (
          selectedEvents.length === 0 ? (
            <EmptyState
              icon="📅"
              title={t("events.empty.title")}
              subtitle={t("events.empty.subtitle")}
              buttonLabel={t("events.create.submit")}
              onButtonClick={handleCreateEvent}
            />
          ) : (
            <div className="space-y-2">
              {selectedEvents.map((ev) => (
                <EventRow
                  key={ev.uid}
                  ev={ev}
                  locale={locale}
                  t={t}
                  confirmDeleteId={confirmDeleteId}
                  setConfirmDeleteId={setConfirmDeleteId}
                  onEdit={handleEditEvent}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )
        ) : eventsByMonth.length === 0 ? (
          <EmptyState
            icon="📅"
            title={t("events.empty.title")}
            subtitle={
              searchText.trim().length > 0
                ? t("events.search.empty")
                : t("events.empty.subtitle")
            }
            buttonLabel={
              searchText.trim().length > 0 ? undefined : t("events.create.submit")
            }
            onButtonClick={
              searchText.trim().length > 0 ? undefined : handleCreateEvent
            }
          />
        ) : (
          <div className="space-y-4">
            {eventsByMonth.map((group) => (
              <div key={group.key} id={`events-month-${group.key}`} className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 px-1">
                  {group.label}
                </p>
                <div className="space-y-2">
                  {group.events.map((ev) => (
                    <EventRow
                      key={ev.uid}
                      ev={ev}
                      locale={locale}
                      t={t}
                      confirmDeleteId={confirmDeleteId}
                      setConfirmDeleteId={setConfirmDeleteId}
                      onEdit={handleEditEvent}
                      onDelete={handleDelete}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Jump-to-today FAB (list view only) */}
      {view === "list" && eventsByMonth.length > 0 && (
        <button
          type="button"
          onClick={() => {
            const todayKey = `${today.getFullYear()}-${today.getMonth()}`;
            const el = document.getElementById(`events-month-${todayKey}`);
            if (el) {
              el.scrollIntoView({ behavior: "smooth", block: "start" });
            }
          }}
          className="fixed bottom-24 right-6 sm:bottom-8 w-12 h-12 rounded-full bg-[color:var(--accent-color)] text-white shadow-lg flex items-center justify-center hover:opacity-90 transition-opacity"
          aria-label={t("events.jumpToToday")}
          title={t("events.jumpToToday")}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
            <text x="12" y="18" textAnchor="middle" fontSize="9" fill="currentColor" stroke="none">{today.getDate()}</text>
          </svg>
        </button>
      )}

      {showModal && householdId && (
        <CreateEventModal
          householdId={householdId}
          event={editEvent}
          onClose={() => {
            setShowModal(false);
            setEditEvent(null);
          }}
        />
      )}

      {showSyncSettings && (
        <CalendarSyncSettingsModal onClose={() => setShowSyncSettings(false)} />
      )}
    </div>
  );
}

interface EventRowProps {
  ev: CalendarEvents;
  locale: string;
  t: (key: string, params?: Record<string, string | number>) => string;
  confirmDeleteId: string | null;
  setConfirmDeleteId: (id: string | null) => void;
  onEdit: (ev: CalendarEvents) => void;
  onDelete: (id: string) => void;
}

function EventRow({
  ev,
  locale,
  t,
  confirmDeleteId,
  setConfirmDeleteId,
  onEdit,
  onDelete,
}: EventRowProps) {
  const start = fromTimestamp(ev.start);
  const end = fromTimestamp(ev.end);
  const readOnly = isReadOnlySource(ev.sync_source);
  const sourceLabel = ev.sync_label
    ? ev.sync_label
    : ev.sync_source === "ical"
    ? t("events.source.ical")
    : ev.sync_source === "eventkit"
    ? t("events.source.deviceCalendar")
    : null;

  return (
    <Card>
      <div className="flex items-start justify-between">
        <button onClick={() => onEdit(ev)} className="flex-1 text-left">
          <p className="font-semibold text-sm">
            {ev.text || ev.summary || t("events.untitled")}
          </p>
          {sourceLabel && (
            <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/20 text-[10px] font-medium text-blue-700 dark:text-blue-300 uppercase tracking-wider">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              {sourceLabel}
            </span>
          )}
          {start && (
            <p className="text-xs text-gray-500 mt-0.5">
              {isAllDayEvent(ev.all_day, start, end)
                ? t("events.allDayBadge")
                : `${formatTime(start, locale)}${
                    end && !sameDay(start, end)
                      ? ` — ${end.toLocaleDateString(locale, {
                          month: "short",
                          day: "numeric",
                        })} ${formatTime(end, locale)}`
                      : end
                      ? ` — ${formatTime(end, locale)}`
                      : ""
                  }`}
            </p>
          )}
          {ev.location && (
            <p className="text-xs text-gray-400 mt-0.5">📍 {ev.location}</p>
          )}
          {ev.description && (
            <p className="text-xs text-gray-400 mt-1 line-clamp-2">{ev.description}</p>
          )}
        </button>
        <div className="flex items-center gap-1 shrink-0 ml-2">
          {/* Hide delete entirely for synced calendar events — they're
              mirrored from a remote source we don't own. */}
          {!readOnly &&
            (confirmDeleteId === ev.uid ? (
              <>
                <button
                  onClick={() => onDelete(ev.uid!)}
                  className="text-xs text-red-500 font-medium px-2 py-1"
                >
                  {t("events.actions.delete")}
                </button>
                <button
                  onClick={() => setConfirmDeleteId(null)}
                  className="text-xs text-gray-400 px-2 py-1"
                >
                  {t("events.actions.cancel")}
                </button>
              </>
            ) : (
              <button
                onClick={() => setConfirmDeleteId(ev.uid!)}
                className="text-gray-300 hover:text-red-400 transition-colors p-1"
                aria-label={t("events.actions.delete")}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 6h18" />
                  <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                  <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                </svg>
              </button>
            ))}
        </div>
      </div>
    </Card>
  );
}
